/** @file This file contains the store for Rubrics. */
import * as api from '@codegrade/api';
import { Maybe } from '@codegrade/maybe';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as models from '@/models';
import * as utils from '@/utils';
import { SubmissionsStore } from './submissions';
const storeBuilder = getStoreBuilder();
/** @returns The initial state. */
function makeInitialState() {
    return {
        rubricLoaders: {},
        rubrics: {},
        resultsForAssignment: {},
        resultLoaders: {},
        results: {},
    };
}
const moduleBuilder = storeBuilder.module('rubrics', makeInitialState());
/** The store for the Rubrics. */
export var RubricsStore;
(function (RubricsStore) {
    const commitClearAll = moduleBuilder.commit(state => Object.assign(state, makeInitialState()), 'commitClearAll');
    /** Commit a rubric to the store. */
    RubricsStore.commitRubric = moduleBuilder.commit((state, { assignmentId, rubric, }) => {
        utils.vueSet(state.rubrics, assignmentId, rubric);
    }, 'commitRubric');
    /** Commit a loader for a rubric to the store. */
    RubricsStore.commitRubricLoader = moduleBuilder.commit((state, { assignmentId, loader, }) => {
        utils.vueSetOrDelete(state.rubricLoaders, assignmentId, loader);
    }, 'commitRubricLoader');
    const commitResult = moduleBuilder.commit((state, { assignmentId, result, }) => {
        utils.vueSet(state.results, result.submissionId, result);
        if (assignmentId != null) {
            utils
                .vueSetDefault(state.resultsForAssignment, assignmentId, [])
                ?.push(result.submissionId);
        }
    }, 'commitResult');
    /** Remove the rubric for the given assignment from the store. */
    RubricsStore.commitClearRubric = moduleBuilder.commit((state, { assignmentId, }) => {
        utils.vueDelete(state.rubrics, assignmentId);
    }, 'commitClearRubric');
    /** Remove the results for the given assignment from the store. */
    RubricsStore.commitClearAssignmentResults = moduleBuilder.commit((state, { assignmentId, }) => {
        for (const submissionId of state.resultsForAssignment[assignmentId] ?? []) {
            utils.vueDelete(state.results, submissionId);
        }
        utils.vueDelete(state.resultsForAssignment, assignmentId);
    }, 'commitClearAssignmentResults');
    /** Commit the loader for a single result to the store. */
    RubricsStore.commitResultLoader = moduleBuilder.commit((state, { submissionId, loader, }) => {
        utils.vueSetOrDelete(state.resultLoaders, submissionId, loader);
    }, 'commitResultLoader');
    /** Get the rubric of an assignment from the store. */
    RubricsStore.getRubric = moduleBuilder.read(state => 
    /**
     * Get the rubric for the given assignment from the store.
     *
     * @param assignmentId The id of the assignment to get the rubric for.
     * @returns The rubric, wrapped in a `Maybe` to indicate whether the
     * rubric is present in the store.
     */
    (assignmentId) => Maybe.fromNullable(state.rubrics[assignmentId]), 'getRubric');
    /** Load the rubric of an assignment. */
    RubricsStore.loadRubric = moduleBuilder.dispatch(async (ctx, { assignmentId, force = false, }) => {
        await utils.storeLoadWithMaybeForce({
            addLoader: loader => RubricsStore.commitRubricLoader({ assignmentId, loader: Maybe.of(loader) }),
            value: Maybe.fromNullable(ctx.state.rubrics[assignmentId]),
            force,
            load: async () => {
                const rows = await Array.fromAsync(utils.loadAllPages(nextToken => client.p.assignment.getRubric({
                    assignmentId,
                    nextToken: nextToken.extract(),
                })));
                RubricsStore.commitRubric({ assignmentId, rubric: models.Rubric.fromApiServerData(rows) });
            },
            loader: Maybe.fromNullable(ctx.state.rubricLoaders[assignmentId]),
            removeLoader: () => RubricsStore.commitRubricLoader({ assignmentId, loader: Maybe.empty() }),
        });
    }, 'loadRubric');
    /** Update the rubric of an assignment. */
    RubricsStore.updateRubric = moduleBuilder.dispatch(async (_, { assignmentId, rubric, maxPoints, }) => {
        const response = await client.p.assignment.putRubric({
            assignmentId,
            requestBody: {
                rows: rubric.rows.map(row => ({
                    ...row,
                    items: row.items.map(item => {
                        if (typeof item.points !== 'number') {
                            throw new Error('Cannot create rubric with non points item');
                        }
                        return {
                            ...item,
                            points: item.points,
                            id: item.id === -1 ? undefined : item.id,
                        };
                    }),
                })),
                maxPoints,
            },
        });
        RubricsStore.commitClearAssignmentResults({ assignmentId });
        RubricsStore.commitRubric({
            assignmentId,
            rubric: rubric.updateFromApiServerData(response.cgResult),
        });
        return response;
    }, 'updateRubric');
    /** Delete the rubric of the given assignment. */
    RubricsStore.deleteRubric = moduleBuilder.dispatch(async (__dirname, { assignmentId, }) => {
        const response = await client.p.assignment.deleteRubric({ assignmentId });
        RubricsStore.commitClearAssignmentResults({ assignmentId });
        RubricsStore.commitRubric({
            assignmentId,
            rubric: models.Rubric.fromApiServerData([]),
        });
        return response;
    }, 'deleteRubric');
    /** Copy a rubric from one assignment to another. */
    RubricsStore.copyRubric = moduleBuilder.dispatch(async (_, { intoAssignment, fromAssignment, }) => {
        const response = await client.p.assignment.copyRubric({
            assignmentId: intoAssignment.id,
            requestBody: {
                oldAssignmentId: fromAssignment.id,
            },
        });
        RubricsStore.commitClearAssignmentResults({ assignmentId: intoAssignment.id });
        RubricsStore.commitRubric({
            assignmentId: intoAssignment.id,
            rubric: models.Rubric.fromApiServerData(response.body),
        });
        return response;
    }, 'copyRubric');
    /** Get the result for a single submission from the store. */
    RubricsStore.getResult = moduleBuilder.read(state => 
    /**
     * Get the rubric result for the given submission from the store.
     *
     * @param submissionId The id of the submission to get the result for.
     * @returns The result, wrapped in a `Maybe` to indicate whether the
     * value is present in the store.
     */
    (submissionId) => Maybe.fromNullable(state.results[submissionId]), 'getResult');
    /** Load the rubric result for the given assignment and submission. */
    RubricsStore.loadResult = moduleBuilder.dispatch(async (ctx, { assignmentId, submissionId, force = false, }) => {
        if (RubricsStore.getRubric()(assignmentId).mapOrDefault(v => v.isEmpty, false)) {
            commitResult({
                result: models.RubricResult.fromApiServerData(submissionId, []),
                assignmentId,
            });
        }
        await utils.storeLoadWithMaybeForce({
            addLoader: loader => RubricsStore.commitResultLoader({ submissionId, loader: Maybe.of(loader) }),
            value: Maybe.fromNullable(ctx.state.results[submissionId]),
            force,
            load: async () => {
                let data;
                try {
                    data = await Array.fromAsync(utils.loadAllPages(nextToken => client.p.submission.getAllRubricResults({
                        submissionId,
                        nextToken: nextToken.extract(),
                    })));
                }
                catch (caught) {
                    if (caught instanceof api.ApiResultError &&
                        caught.response.status === 403) {
                        data = [];
                    }
                    else {
                        throw caught;
                    }
                }
                commitResult({
                    result: models.RubricResult.fromApiServerData(submissionId, data),
                    assignmentId,
                });
            },
            loader: Maybe.fromNullable(ctx.state.resultLoaders[submissionId]),
            removeLoader: () => RubricsStore.commitResultLoader({ submissionId, loader: Maybe.empty() }),
        });
    }, 'loadResult');
    /** Update a rubric result in the backend and update the store with the updated result. */
    RubricsStore.updateResult = moduleBuilder.dispatch(async (_, { result, rubric, }) => {
        const items = result.getManuallyMarkedItems(rubric);
        const response = await client.p.submission.putRubricResult({
            submissionId: result.submissionId,
            copyLockedItems: true,
            requestBody: {
                items,
            },
        });
        commitResult({
            result: new models.RubricResult(result.submissionId, items.concat(response.body.copiedItems)),
            assignmentId: null,
        });
        SubmissionsStore.commitFullSubmission({
            data: response.body.submission,
        });
        return response;
    }, 'updateResult');
    utils.signal.AFTER_USER_CHANGED.register(async () => {
        commitClearAll();
    });
})(RubricsStore || (RubricsStore = {}));
